import { useState, useEffect, useMemo, Component, ErrorInfo, ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'
import { normalizeTablesSimple, hasMalformedTables } from '@/lib/table-normalizer'
import { TableFallback, ErrorFallback } from './table-fallback'

/**
 * MarkdownRenderer - Safe full-featured markdown display with error handling
 *
 * **USE WHEN:** You need to display markdown content safely in read-only mode
 *
 * **LIBRARY:** react-markdown (with error boundaries and table normalization)
 *
 * **FEATURES:**
 * - Full markdown rendering (tables, lists, code blocks, etc.)
 * - Error boundary (catches parsing errors)
 * - Table normalization (fixes malformed tables)
 * - Fallback display for errors
 * - GitHub Flavored Markdown support (GFM)
 * - Dark mode support
 *
 * **EXAMPLES:**
 * - FAQ answer display (faq-detail.tsx, faq-review-card.tsx)
 * - System prompt display
 * - Any read-only markdown content
 *
 * **DON'T USE FOR:**
 * - Editing → use MarkdownEditor instead
 * - Compact preview → use MarkdownPreview instead
 */

// Simple inline error boundary for markdown rendering
class MarkdownErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Markdown parsing error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const [renderError, setRenderError] = useState(false)

  // Simple AST-based normalization (replaces all regex hacks)
  const normalizedContent = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return ''
    }

    try {
      return normalizeTablesSimple(content)
    } catch (error) {
      console.warn('Content normalization failed:', error)
      return content
    }
  }, [content])

  // Check if we need to show a table-specific fallback
  const hasTableIssues = useMemo(() => {
    return hasMalformedTables(content)
  }, [content])

  // Handle empty content
  if (!normalizedContent) {
    return (
      <div className={cn('text-sm text-muted-foreground italic', className)}>
        Kein Inhalt verfügbar
      </div>
    )
  }

  // Fallback for rendering errors
  if (renderError) {
    return <ErrorFallback content={content} className={className} />
  }

  return (
    <MarkdownErrorBoundary
      fallback={<ErrorFallback content={content} className={className} />}
    >
      <div className={cn('prose prose-neutral dark:prose-invert max-w-none', className)}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {normalizedContent}
        </ReactMarkdown>
      </div>
    </MarkdownErrorBoundary>
  )
}